import { AuthenticationService, LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import SmartcareTranscription, { RecordingState } from './smartcare-transcription';
import { SmartcareCloudTranscriptionService, type TranscriptionOptions, type TranscriptionRequest  } from '@philips-internal/cci-platform-smartcare-web-services';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import ShowToast from '../../../../utils/toast-service';
import { SmartcareActions } from './smartcare-actions';

export class SmartCareStreamTranscription extends SmartcareTranscription implements SmartcareActions {
  private smartcareUrl: string;
  private smartcareStreamService: SmartcareCloudTranscriptionService;
  private readonly tags: {[key:string]:string};

  constructor(smartcareUrl: string, tags: {[key:string]:string} = {}) {
    super();
    this.tags = tags;
    this.smartcareUrl = this.getSmartCareUrl(smartcareUrl);
    this.smartcareStreamService = this.createSmartCareStreamService();
  }

  public async start(): Promise<void> {
    if (this.audiRecordService?.state === RecordingState.RECORDING) {
      this.stop();
    }

    try {
      await this.audiRecordService.start();
    } catch (error) {
      ShowToast.open(MessageService.translate('text.microphone-not-recognized'), ToastLevel.ERROR);
      return Promise.reject(new Error(MessageService.translate('text.microphone-not-recognized')))
    }
    this.timerService.startTimer()
    await this.smartcareStreamService.start(this.audiRecordService.stream as MediaStream);
    this.addTranscriptEvent();
    return Promise.resolve();
  }

  public async pause(): Promise<void> {
    if (this.audiRecordService?.state === RecordingState.RECORDING) {
      this.audiRecordService.pause();
      this.timerService.stopTimer()
      return new Promise((resolve) => {
        setTimeout(() =>{
          this.smartcareStreamService.stop()
          resolve()
        }, 4000)
      })
    }
    return Promise.reject(new Error('Recording is not active'))
  }

  public async resume(): Promise<void> {
    if (this.audiRecordService) {
      await this.smartcareStreamService.start(this.audiRecordService.stream as MediaStream);
      this.audiRecordService.resume();
      this.timerService.startTimer()
    }
  }

  public async stop(): Promise<void> {
    if (this.audiRecordService?.state !== RecordingState.INACTIVE) {
      this.audiRecordService.clearEventListeners();
      await this.audiRecordService.stop();
      await this.closeSmartCareConnection();
    }
  }

  private getSmartCareUrl(smartcareUrl: string): string {
    const transcriptionStreamApi = '/api/services/transcription/stream';
    const smartCareWebSocketUrl = this.setWebSocketProtocol(smartcareUrl)
    return `${smartCareWebSocketUrl}${transcriptionStreamApi}`
  }

  private setWebSocketProtocol(url: string): string {
    if (!url) return '';
    const newUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    return newUrl.startsWith('https') ? newUrl.replace('https', 'wss') : newUrl.replace('http', 'ws');
  }

  private async closeSmartCareConnection(): Promise<void> {
    if (this.smartcareStreamService) {
      this.smartcareStreamService.clearEventListeners();
      await this.smartcareStreamService.stop();
    }
  }

  private createSmartCareStreamService(): SmartcareCloudTranscriptionService {
    const language = LanguageService.getCurrentLanguage();

    let options = {
      language: 'pt-BR',
      speakerRecognitionEnabled: true,
      tags: this.tags
    } as TranscriptionOptions;

    if (language.toLowerCase() !== 'pt-br') {
      options = {
        ...options,
        language: 'en-US',
        specialty: 'PRIMARYCARE',
        type: 'CONVERSATION'
      };
    }

    const token = AuthenticationService.getJWTToken()?.rawToken();

    const transcriptionRequest = {
      endpoint: this.smartcareUrl,
      token: `Bearer ${token}`
    } as TranscriptionRequest;

    return new SmartcareCloudTranscriptionService(transcriptionRequest, options);
  }

  private addTranscriptEvent(): void {
    this.smartcareStreamService.addEventListener('transcript', async (transcript) => {
      if (!transcript.partial && transcript.speaker !== null) {
        super.addTranscription({
          speaker: transcript.speaker,
          timer: this.timerService.getTimer(),
          transcript: transcript.transcription,
        });
      }
    });
  }
}
